// src/components/.../ProductIntroTopSection.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { getLangFromCookie, LANG_COOKIE } from "../../../../Utils/LangCookie";
import AreaDiagram from "./AreaDiagram";
import CategoryCard from "./CategoryCard";
import { useAuth } from "../../Auth/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";

/* ───────────── helpers ───────────── */
const ensureSlash = (p) => {
  const s = String(p || "");
  if (!s) return s;
  return s.startsWith("/") ? s : "/" + s.replace(/^\/+/, "");
};

const safeDecode = (s) => {
  try {
    return decodeURI(String(s || ""));
  } catch (e) {
    return String(s || "");
  }
};

const getCurrentLang = () =>
  getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH";

const byLang = (item, lang) =>
  lang === "EN"
    ? item.EN || item.label
    : lang === "JP"
      ? item.JP || item.label
      : item.label;

const SECTION_TITLES = {
  industry: { ZH: "產業類別", EN: "Industry Area", JP: "業種から探す" },
  human: { ZH: "人體接觸", EN: "Body Area", JP: "身体の部位から探す" },
  area: { ZH: "使用區域", EN: "Usage Area", JP: "使用エリア" },
};

/* ───────────── data ───────────── */
const INDUSTRY_ITEMS = [
  {
    key: "food-factory",
    label: "食品工廠",
    EN: "Food Manufacturing",
    JP: "食品製造工場",
    to: "/商品資訊/產業類別/食品工廟",
    img: "/img/products/B2B/industry01.webp",
  },
  {
    key: "chain-restaurant",
    label: "餐飲連鎖",
    EN: "Restaurant Chain",
    JP: "外食チェーン事業者",
    to: "/商品資訊/產業類別/餐飲連鎖",
    img: "/img/products/B2B/industry02.webp",
  },
  {
    key: "hyper",
    label: "量販店",
    EN: "Hypermarkets",
    JP: "量販店",
    to: "/商品資訊/產業類別/量販店",
    img: "/img/products/B2B/industry03.webp",
  },
  {
    key: "super-conv",
    label: "超市/便利超商",
    EN: "Supermarkets / Convenience Stores",
    JP: "スーパーマーケット／コンビニエンスストア",
    to: "/商品資訊/產業類別/超市|便利超商",
    img: "/img/products/B2B/industry04.webp",
  },
  {
    key: "hotel",
    label: "飯店/民宿",
    EN: "Hotels",
    JP: "ホテル・旅館/民泊・簡易宿所",
    to: "/商品資訊/產業類別/飯店|民宿",
    img: "/img/products/B2B/industry05.webp",
  },
];

const HUMAN_PARTS = [
  {
    key: "head",
    label: "頭",
    EN: "Head",
    JP: "頭部",
    to: "/商品資訊/人體接觸/頭",
    img: "/img/products/B2B/head.webp",
    objectfit: "cover",
  },
  {
    key: "hand",
    label: "手",
    EN: "Hands",
    JP: "手",
    to: "/商品資訊/人體接觸/手",
    img: "/img/products/B2B/hand.webp",
    objectfit: "cover",
  },
  {
    key: "body",
    label: "身",
    EN: "Body",
    JP: "体",
    to: "/商品資訊/人體接觸/身體",
    img: "/img/products/B2B/body.webp",
    objectfit: "cover",
  },
  {
    key: "foot",
    label: "腳",
    EN: "Feet",
    JP: "足元",
    to: "/商品資訊/人體接觸/腳",
    img: "/img/products/B2B/foot.webp",
    objectfit: "cover",
  },
];

export default function ProductIntroTopSection({
  onSelectCategory,
  currentHref,
  cardsOnly = false,
  forceShowAllSections = false,
  forceSectionKey = "",
}) {
  const [lang, setLang] = useState(getCurrentLang());
  const { user } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const onChange = () => setLang(getCurrentLang());
    window.addEventListener("app:languageChanged", onChange);
    return () => window.removeEventListener("app:languageChanged", onChange);
  }, []);

  const isActiveHref = (target) => {
    if (!currentHref) return false;
    const a = safeDecode(currentHref);
    const b = safeDecode(target);
    return a === b || a.startsWith(b + "/");
  };

  const normHref = safeDecode(ensureSlash(currentHref || ""));
  const sectionKey = useMemo(() => {
    if (normHref.includes("/產業類別")) return "industry";
    if (normHref.includes("/人體接觸")) return "human";
    if (normHref.includes("/使用區域")) return "area";
    return "all";
  }, [normHref]);

  const activeSectionKey = forceSectionKey || sectionKey;

  const showIndustrySection =
    forceShowAllSections ||
    activeSectionKey === "all" ||
    activeSectionKey === "industry";

  const showHumanSection =
    forceShowAllSections ||
    activeSectionKey === "all" ||
    activeSectionKey === "human";

  const showAreaSection =
    forceShowAllSections ||
    activeSectionKey === "all" ||
    activeSectionKey === "area";

  // ✅ NEW: no more scrolling-to-titles here.
  // We just select category and dispatch event; ProductInfoPage will scroll to chip bar.
  const handleSelectCategory = (to) => {
    const href = ensureSlash(to);
    onSelectCategory?.(href);

    window.dispatchEvent(
      new CustomEvent("app:categorySelected", { detail: { to: href } }),
    );
  };

  const industryTitle =
    SECTION_TITLES.industry[lang] || SECTION_TITLES.industry.ZH;
  const humanTitle = SECTION_TITLES.human[lang] || SECTION_TITLES.human.ZH;
  const areaTitle = SECTION_TITLES.area[lang] || SECTION_TITLES.area.ZH;

  return (
    <Box
      component="section"
      sx={{
        bgcolor: "background.paper",
        py: { xs: 2, md: 3 },
        width: "100%",
        mx: 0,
      }}
    >
      {/* 產業類別 */}
      {/* {showIndustrySection && (
        <Box
          sx={
            cardsOnly
              ? { mb: 4 }
              : sectionKey === "industry"
                ? { py: 13 }
                : { mb: 4 }
          }
        >
          <Divider sx={{ color: "black" }}>
            <Typography
              align="center"
              sx={{ fontWeight: 700, fontSize: { xs: 18, md: 25 }, mb: 2 }}
            >
              {industryTitle}
            </Typography>
          </Divider>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            {INDUSTRY_ITEMS.map((it) => (
              <CategoryCard
                key={it.key}
                label={byLang(it, lang)}
                img={it.img}
                active={isActiveHref(it.to)}
                onClick={() => {
                  if (!user) {
                    navigate("/auth", {
                      replace: false,
                      state: { redirectTo: loc.pathname },
                    });
                    return;
                  }
                  handleSelectCategory(it.to);
                }}
                objfit="cover"
              />
            ))}
          </Box>
        </Box>
      )} */}

      {/* 人體接觸 */}
      {/* {showHumanSection && (
        <Box
          sx={
            cardsOnly
              ? { mb: 4 }
              : sectionKey === "human"
                ? { py: 13 }
                : { mb: 4 }
          }
        >
          <Divider sx={{ color: "black" }}>
            <Typography
              align="center"
              sx={{ fontWeight: 700, fontSize: { xs: 18, md: 25 } }}
            >
              {humanTitle}
            </Typography>
          </Divider>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            {HUMAN_PARTS.map((p) => (
              <CategoryCard
                key={p.key}
                label={byLang(p, lang)}
                img={p.img}
                active={isActiveHref(p.to)}
                onClick={() => handleSelectCategory(p.to)}
                objfit={p.objectfit}
              />
            ))}
          </Box>
        </Box>
      )} */}

      {/* 使用區域 */}
      {showAreaSection && (
        <Box
          sx={
            cardsOnly
              ? undefined
              : sectionKey === "area"
                ? { pt: 4, pb: 0 }
                : undefined
          }
        >
          <Divider sx={{ color: "black" }}>
            <Typography
              align="center"
              sx={{
                fontWeight: 700,
                fontSize: { xs: 18, sm: 24, md: 28, lg: 25 },
              }}
            >
              {areaTitle}
            </Typography>
          </Divider>

          <AreaDiagram
            lang={lang}
            selectedHref={currentHref}
            onSelectCategory={(to) => handleSelectCategory(to)}
            cardsOnly={cardsOnly}
          />
        </Box>
      )}
    </Box>
  );
}
